import React, { useCallback, useState } from "react";
import _, { get, isEmpty } from "lodash";
import { connect } from "react-redux";
import T from "prop-types";
import { Link } from "react-router-dom";
import {
  itemInfo,
  MISTAKE,
  REJECTED_CORE_ORDER,
  RETURN_TO_CUSTOMER_RECORD,
  RMA
} from "MODULES/Orders/shared/constants";
import {
  CheckColumn,
  DataTable,
  SearchPropTypes,
  ToggleButton,
  PartNumber,
  CompanyName,
  IconText,
  NoteTooltip
} from "MODULES/shared/components";
import { StatusTag } from "MODULES/shared/components/StatusTags";
import {
  UNDER_REVIEW,
  QC_HOLD,
  RETURNED_CONSIGNMENT
} from "MODULES/inventories/constants";
import { condition } from "MODULES/SalesOrders/constants";
import { LINK_MAPPINGS } from "MODULES/Shipping/constants";
import { shortDateYYandTime } from "LIB/formatters/date";
import { getFirstNameLastInitial, truncate } from "LIB/formatters/text";
import LocationFlag from "MODULES/shared/components/LocationFlag/LocationFlag";
import NoteTooltipTableCell from "MODULES/shared/components/NoteTooltip/NoteTooltipTableCell";
import UpdatedTrackedFieldsAt from "MODULES/shared/components/UpdatedTrackedFieldsAt/UpdatedTrackedFieldsAt";
import { USER } from "MODULES/users/constants";
import { CARRIERS } from "MODULES/shared/constants";
import MultiLocationFeature from "MODULES/shared/components/MultiLocationFeature/MultiLocationFeature";
import PrintInventory from "APP_ROOT/modules/inventories/components/Detail/Header/DropdownActions/Print/Print";
import api from "APP_ROOT/api";
import { fromJS } from "immutable";
import { toggleAsyncPicked } from "../../actions";

const abbrev = t => itemInfo(t).abbrev || t;

const DiscreetAttached = ({ order }) => {
  if (order.discreet_attached) {
    return (
      <IconText
        note="Customer's discreet paperwork provided"
        margin="5px"
        icon="text-default fa-user-secret"
      />
    );
  }

  return null;
};

DiscreetAttached.propTypes = {
  order: T.shape({})
};

function Table({ openModal, results, pick, currentUser, isLoading }) {
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [inventory, setInventory] = useState(null);
  const openOrder = useCallback(
    order => e => {
      e.stopPropagation();
      openModal(order);
    },
    []
  );

  function handlePrintIconClick(e, order) {
    e.preventDefault();
    e.stopPropagation();
    api.inventories.show(order.inventory_id).then(response => {
      setInventory(fromJS(response.data));
      setIsPrintModalOpen(true);
    });
  }

  return (
    <React.Fragment>
      {isPrintModalOpen &&
        inventory && (
          <PrintInventory
            editing={isPrintModalOpen}
            close={() => setIsPrintModalOpen(false)}
            inventory={inventory}
          />
        )}

      <DataTable loading={isLoading}>
        <DataTable.Head>
          <th />
          <th>Ref #</th>
          <th>Date Created</th>
          <th>Part #</th>
          <th>Serial #</th>
          <MultiLocationFeature>
            <th>Store Loc</th>
          </MultiLocationFeature>
          <th>Location</th>
          <th>Company</th>
          <th>Cust PO #</th>
          <th>Discreet</th>
          <th>Pics Req&apos;d</th>
          <th>Cust P/W</th>
          <th>Carrier</th>
          <th>Outgoing AWB#</th>
          <th>Spec Instr</th>
          <th>Status</th>
          <th>CD</th>
          <th className="text-right">QTY</th>
          <th className="text-right">QTY Ship</th>
          <th>Picked At</th>
          <th>Picked By</th>
          <th width="1%">Print</th>
          <th width="2%" />
        </DataTable.Head>
        <DataTable.Body>
          {results.map(order => {
            const isMistakeShipment =
              order.company_name &&
              order.company_name.toLowerCase() === "mistake shipment";
            return (
              <tr
                className="clickable"
                key={`${order.type}-${order.order_id}-${order.id}`}
                onClick={openOrder(order)}
              >
                <td>
                  {order.exporting && (
                    <NoteTooltip
                      icon="fa-globe"
                      placement="top"
                      note="Exporting"
                      color="#2196F3"
                    />
                  )}
                </td>
                <td>
                  {(order.parent || [RMA, MISTAKE].includes(order.type)) && (
                    <Link
                      to={`/${LINK_MAPPINGS[abbrev(order.type)]}/${[
                        MISTAKE,
                        RMA
                      ].includes(order.type)
                        ? order.uuid
                        : order.parent.uuid || order.parent.id}`}
                    >
                      {[RMA, MISTAKE].includes(order.type)
                        ? `${abbrev(order.type)}-${order.code}`
                        : order.parent.code}
                    </Link>
                  )}
                  {order.type === REJECTED_CORE_ORDER && (
                    <Link to={`/rejected-cores/${order.uuid}`}>
                      RC-{order.code}
                    </Link>
                  )}
                  {order.type === RETURNED_CONSIGNMENT && (
                    <span>{order.consignment_reference}</span>
                  )}
                  {order.type === RETURN_TO_CUSTOMER_RECORD && (
                    <Link to={`/inventories/${order.inventory_uuid}`}>
                      RMA-{order.code}
                    </Link>
                  )}
                </td>
                <td>
                  <UpdatedTrackedFieldsAt
                    record={order}
                    formatter={shortDateYYandTime}
                  />
                </td>
                <td>
                  <PartNumber
                    part={{
                      id: order.part_id,
                      part_number: order.part_number,
                      uuid: order.part_uuid
                    }}
                    statsTooltip={false}
                  />
                </td>
                <td>
                  {!isMistakeShipment && (
                    <Link
                      to={`/inventories/${order.inventory_uuid ||
                        order.inventory_id}`}
                    >
                      {order.serial_number}
                    </Link>
                  )}
                </td>
                <MultiLocationFeature>
                  <td className="text-center">
                    <LocationFlag location={order.store_location_id} />
                  </td>
                </MultiLocationFeature>
                <td>
                  {order.type === REJECTED_CORE_ORDER
                    ? truncate(order.location, 15)
                    : truncate(order.inventory_location, 15)}
                  {order.received_virtually && (
                    <span style={{ marginLeft: 5 }}>
                      <NoteTooltip note="Virtual" />
                    </span>
                  )}
                </td>
                <td>
                  <CompanyName
                    flattenedCompany={order}
                    statsTooltip={false}
                    truncateAt={25}
                  />
                </td>
                <td>{order.customer_purchase_order_number}</td>
                <CheckColumn
                  checked={order.discreet}
                  icon="eye-slash"
                  text="Yes"
                />
                <CheckColumn
                  checked={get(order, "parent.pictures_required")}
                  colorClass="text-success text-center"
                  icon="check"
                  text="Yes"
                />
                <CheckColumn
                  checked={order.customer_to_provide_own_paperwork}
                  icon="check"
                  text="Yes"
                >
                  <DiscreetAttached order={order} />
                </CheckColumn>
                <td className={order.carrier_does_not_match && "text-yellow"}>
                  {order.carrier && CARRIERS[order.carrier].label}
                </td>
                <td>{truncate(order.outgoing_awb_number, 15)}</td>
                <NoteTooltipTableCell note={order.special_instructions} />
                <td>
                  <div className="flex-tags">
                    {order.export_compliance_hold && (
                      <StatusTag title="Export Compliance Hold" icon="pause" />
                    )}
                    {order.new_company_hold && (
                      <StatusTag title="New Cust. Hold" icon="pause" />
                    )}
                    {order.customer_qc_preapproval_required && (
                      <StatusTag title="QC Pre-Approval Req'd" icon="pause" />
                    )}
                    {order.export_hold && (
                      <StatusTag title="Export Hold" icon="pause" />
                    )}
                    {order.accounting_hold && (
                      <StatusTag title="Accounting Hold" icon="pause" />
                    )}
                    {(order.qc_status === QC_HOLD || order.qc_hold) && (
                      <StatusTag
                        title="QC Hold"
                        icon="warning"
                        display="danger"
                      />
                    )}
                    {order.qc_status === UNDER_REVIEW && (
                      <StatusTag
                        title="Under Review"
                        icon="search"
                        display="warning"
                      />
                    )}
                    {!order.customer_qc_preapproval_required &&
                      !order.accounting_hold &&
                      !order.new_company_hold &&
                      !order.export_compliance_hold &&
                      !order.export_hold &&
                      order.qc_status !== QC_HOLD &&
                      !order.qc_hold &&
                      order.qc_status !== UNDER_REVIEW && (
                        <StatusTag
                          title="Released"
                          icon="truck"
                          display="success"
                        />
                      )}
                  </div>
                </td>
                <td>{condition(order.condition).abbrev}</td>
                <td className="text-right">{order.quantity}</td>
                <td className="text-right">{order.quantity_shipped}</td>
                <td>
                  {order.picked &&
                    shortDateYYandTime(
                      _.get(order, "picked_details.picked_at")
                    )}
                </td>
                <td>
                  {order.picked &&
                    getFirstNameLastInitial(
                      _.get(order, "picked_details.picked_by.name")
                    )}
                </td>
                <td className="center-center">
                  {order.inventory_id && (
                    <i
                      className="fa fa-print"
                      onClick={e => handlePrintIconClick(e, order)}
                    />
                  )}
                </td>
                <td width="2%" style={{ position: "relative" }}>
                  {order.received_virtually ? (
                    "Dropshipment"
                  ) : (
                    <ToggleButton
                      text="Pick"
                      toggledText="Unpick"
                      toggled={order.picked}
                      canRemove={
                        _.get(order, "picked_details.picked_by.id") !==
                          currentUser.id || order.picked
                      }
                      handleClick={e => {
                        pick(order);
                        e.stopPropagation();
                      }}
                    />
                  )}
                  {!isEmpty(order.shipping_cached_key) && (
                    <span style={{ position: "absolute", right: "-5px" }}>
                      <NoteTooltip
                        note="Saved Checklist"
                        icon="fa-thumb-tack"
                        color="#2196F3"
                      />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </DataTable.Body>
      </DataTable>
    </React.Fragment>
  );
}

Table.propTypes = {
  openModal: T.func.isRequired,
  pick: T.func.isRequired,
  currentUser: T.shape({}),
  ...SearchPropTypes
};

export default connect(
  state => ({
    currentUser: state.session.get(USER).toJS()
  }),
  (dispatch, ownProps) => ({
    pick: order => dispatch(toggleAsyncPicked(order)),
    refresh: ownProps.pageActions.expire
  })
)(Table);



// WEBPACK FOOTER //
// ./app/modules/Shipping/components/Dashboard/Table.jsx